import React, { useState, useEffect, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  SHEET_HEADERS,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { FaRegClock } from "react-icons/fa";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { createPortal } from "react-dom";
import SimpleCalculator from './SimpleCalculator'; 
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase";

// --- Icons ---
const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

const IconJournal = () => (
  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="#f59e42" strokeWidth="2"/>
      <path d="M8 2v4M16 2v4" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

// --- Constants ---
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell"];
const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];
const AUTO_CALC_FIELDS = ["Initial Balance", "Risk Percentage", "Stop Loss", "Product"];
const numericFields = ["Initial Balance", "Risk Percentage", "Stop Loss", "Take Profit", "Account Goal",  "Risk Dollar Amount", "Daily Goal", "Daily Loss Limit", "Maximum Contracts", "Minimum Contracts"];

// --- Default Settings ---
const defaultSettings = {
  "Theme": "Dark", 
  "Pretrade Checklist": "Yes",
  "Calculator Only": false,
  "Daily Goal": 500, 
  "Daily Loss Limit": 200, 
  "Maximum Contracts": 1000, 
  "Minimum Contracts": 0.01,
  "Maximum Shares": 1000,
  "Minimum Shares": 0.0001,
  "Risk Percentage": 2.00, 
  "Stop Loss": 20, 
  "Take Profit": 100, 
  "Product": "", 
  "Initial Balance": 0,"Account Goal": 0, "Running Balance": 0,
  "Buying Power": "", 
  "Risk Mode": "%",                 
  "Risk Dollar Amount": 0,          
  "Stop Loss Mode": "%", 
  "Take Profit Mode": "%", 
  "Number of Trades to Goal": 0,
  "OvertradingPopupShownDate": "", "DrawdownPopupShownDate": "", "TooManyLossesPopupShownDate": "",
  "DailyGoalPopupShownDate": "", "DailyGoalPopupShownForGoal":"","LossLimitWarningShownDate": "",
  "MoodVolatilityPopupShownDate": "", "OverRiskWarningShownDate": "", "ConsecutiveWinsPopupShownDate": "",
  "AccountGoalReachedShownDate": "", "LessonsLearnedStreakWarningShownDate": "","RiskOfRuinWarningShownDate": "",
  "TwoLossWarningShownDate": "", "ThreeLossWarningShownDate": "", "MaxLossLimitBlownShownDate": "",
  "TwoLossAfterGoalWarningShownDate": "", "Win10NoRulesPopupShownDate": "", "Win10WithRulesPopupShownDate": "",
  "PretradeChecklistShownDate": "", "DrawdownLockActive": false,
  "DrawdownLockStartedDate": "", "DrawdownLockPeak": 0, "Entry Price": 0, "Leverage": 1
};

const defaultJournal = {
  "Product": null,
  "Actual Balance": "",
  "Position": "",
  "Date": "",
  "Session": "",
  "Entry Time": "",
  "Exit Time": "",
  "Entry Price": "",
  "Exit Price": "",
  "Entry Strategy": "",
  "Exit Strategy": "",
  "Did I follow my Rules?": "",
  "Size": 0,
  "Tcks": "", 
  "Potential": "",
  "IdealSL":"",
  "Suggested Max # of Contracts": 0, 
  "Win": false,
  "Loss": false,
  "Mood_Mindset": "",
  "Grade": "",
  "Notes": "",
  "Links_Screenshots": "",
  "Lesson": "",
  "LogLesson": false,
  "Timeframe": "",
};

// --- Helpers ---
function getPricePerTick(productStr) { return 1; }
function getTickSizeForProduct(productStr) { return 0.01; }
function computeTicksFromPricesAbs(productStr, entryPriceRaw, exitPriceRaw) {
  const entry = Number(entryPriceRaw);
  const exit = Number(exitPriceRaw);
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;
  return Math.abs(exit - entry); 
}
function formatCurrency(val) {
  if (val === "" || val === null || typeof val === "undefined" || isNaN(val)) return "";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}
function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>
        {value}
      </span>
    </div>
  );
}
// Treat these keys as "clearable numeric"
const RISK_KEYS = new Set(["Risk Percentage", "Risk Dollar", "Risk Amount", "Risk Dollar Amount", "Buying Power"]);
const commitNumberOrEmpty = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) ? n : "";
};
function useBlockWheelOnNumber(inputRef) {
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, [inputRef]);
}
function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (m) {
    let [, h, mins, ap] = m;
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mins}`;
  }
  return "";
}
function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}
function timeStringToSeconds(str) {
  if (!str) return 0;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return (h * 3600) + (m * 60) + (s || 0);
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [, h, m, ap] = match;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return (h * 3600) + (m * 60);
  }
  return 0;
}
function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0"),
  ].join(":");
}
function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}
function clampPct(n) {
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

// Tooltip Component
function Tooltip({ children, content, variant = "neutral", backdrop = false, closeOnEsc = true, lockScroll = true }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!(open && backdrop && closeOnEsc)) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, backdrop, closeOnEsc]);
  useEffect(() => {
    if (!(open && backdrop && lockScroll)) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, backdrop, lockScroll]);
  
  const palette = {
    neutral: { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" },
    yellow: { panel: "bg-yellow-600/95 text-white ring-yellow-300/30", arrow: "bg-yellow-600/95", focus: "focus:ring-yellow-500" },
    red: { panel: "bg-red-800/95 text-white ring-red-300/30", arrow: "bg-red-800/95", focus: "focus:ring-red-500" },
    blue: { panel: "bg-slate-900/95 text-white ring-blue-300/30", arrow: "bg-slate-900/95", focus: "focus:ring-blue-500" },
  }[variant] || { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" };
  
  return (
    <span className="relative inline-flex items-center group" onMouseEnter={() => !backdrop && setOpen(true)} onMouseLeave={() => !backdrop && setOpen(false)}>
      <span tabIndex={0} aria-describedby={id} aria-expanded={open ? "true" : "false"} onClick={() => setOpen((o) => !o)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)} onBlur={() => { if (!backdrop) setOpen(false); }} className={`underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}>{children}</span>
      <span id={id} role={backdrop ? "dialog" : "tooltip"} aria-modal={backdrop ? "true" : undefined} className={`select-text absolute ${backdrop ? "z-[70]" : "z-[60]"} bottom-full left-1/2 -translate-x-1/2 mb-2 max-w-[min(90vw,28rem)] w-max break-words rounded-lg px-4 py-3 text-[13px] leading-relaxed font-normal shadow-2xl ring-1 backdrop-blur-sm transition opacity-0 scale-95 pointer-events-none ${palette.panel} ${open ? "opacity-100 scale-100 pointer-events-auto" : ""} ${backdrop ? "" : "group-hover:opacity-100 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:scale-100"}`}>{typeof content === "string" ? <span className="block">{content}</span> : content}<span aria-hidden="true" className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow`} /></span>
      {open && backdrop && createPortal(<div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px]" onClick={() => setOpen(false)} aria-hidden="true" />, document.body)}
    </span>
  );
}

function localISODay(d) {
  const dt = new Date(d);
  const tz = dt.getTimezoneOffset() * 60000;
  return new Date(dt - tz).toISOString().slice(0, 10);
}
function normalizeDateKey(v) {
  if (!v) return "";
  if (typeof v === "string") {
    const s = v.trim();
    if (s.includes("/")) { const [mm, dd, yyyy] = s.split("/"); return `${yyyy}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`; }
    return s.slice(0, 10);
  }
  if (v?.seconds) return localISODay(v.seconds * 1000);
  try { return localISODay(v); } catch { return ""; }
}

// Projections
function getCompoundingProjection({ balance, goal, riskPercent, riskMode = "%", riskDollar = 0, stopLoss, takeProfit, maxContracts, minContracts, pricePerTick, entryPrice, investmentAmount, investmentCapMode }) {
   let trades = [];
   let currentBalance = balance;
   let tradeCount = 0;
   
   const safeEntryPrice = entryPrice > 0 ? entryPrice : (stopLoss > 0 ? stopLoss * 10 : 100);
   const minCtr = Math.max(0.0001, Number(minContracts) || 0.0001);
   const maxCtr = Math.max(minCtr, Number(maxContracts) || minCtr);

   while (currentBalance < goal && tradeCount < 10000) {
    const fixed = Number(riskDollar) || 0;
    const riskBudget = String(riskMode) === "$" ? fixed : Math.round((currentBalance * riskPercent) / 100 * 100) / 100;
    
    let currentInvestCap = investmentAmount;
    if (investmentCapMode === "%") {
        currentInvestCap = currentBalance * (investmentAmount / 100);
    }
    
    const perShareRisk = stopLoss; 
    let contracts = 0;

    if (stopLoss > 0) {
        const riskSize = riskBudget / stopLoss;
        const cashSize = currentBalance / safeEntryPrice;
        const capSize = (currentInvestCap > 0) ? (currentInvestCap / safeEntryPrice) : 999999;
        contracts = Math.min(riskSize, cashSize, capSize);
    } else {
        const cashSize = currentBalance / safeEntryPrice;
        const capSize = (currentInvestCap > 0) ? (currentInvestCap / safeEntryPrice) : 999999;
        contracts = Math.min(cashSize, capSize);
    }
    
    contracts = Math.floor(contracts * 10000) / 10000;
    contracts = Math.min(contracts, maxCtr);
    
    let usedSize = Math.max(contracts, minCtr);

    // Check if underfunded
    let isUnderfunded = false;
    let tradeCost = usedSize * safeEntryPrice;
    if (tradeCost > currentBalance) isUnderfunded = true;
    
    const tradeProfit = usedSize * takeProfit;
    const tradeRisk = usedSize * perShareRisk; 

    trades.push({
       trade: tradeCount + 1, 
       balance: currentBalance, 
       riskDollar: riskBudget, 
       contracts: usedSize,
       totalRisk: tradeRisk, 
       tradeProfit, 
       nextBalance: currentBalance + tradeProfit,
       underfunded: isUnderfunded,
    });
    
    currentBalance += tradeProfit;
    tradeCount++;
    if (currentBalance >= goal) break;
   }
   return { trades, tradeCount: trades.length };
}

function getLossProjection({ balance, riskPercent, riskMode = "%", riskDollar = 0, stopLoss, maxContracts, minContracts, pricePerTick, entryPrice, investmentAmount, investmentCapMode }) {
   const trades = [];
   const perShareRisk = Number(stopLoss); 
   const minCtr = Math.max(0.0001, Number(minContracts) || 0.0001);
   const maxCtr = Math.max(minCtr, Number(maxContracts) || minCtr);
   const safeEntryPrice = entryPrice > 0 ? entryPrice : (stopLoss > 0 ? stopLoss * 10 : 100);
   let currentBalance = Number(balance) || 0;
   let tradeCount = 0;
   const limit = 1; // Stocks limit (stop at $1.00)

   while (currentBalance > (limit + 0.001) && tradeCount < 10000) {
    const fixed = Number(riskDollar) || 0;
    const riskBudget = String(riskMode) === "$" ? fixed : (Number(riskPercent) / 100) * currentBalance;
    
    let currentInvestCap = investmentAmount;
    if (investmentCapMode === "%") {
        currentInvestCap = currentBalance * (investmentAmount / 100);
    }
    
    let contracts = 0;
    if (stopLoss > 0) {
        const riskSize = riskBudget / stopLoss;
        const cashSize = currentBalance / safeEntryPrice;
        const capSize = (currentInvestCap > 0) ? (currentInvestCap / safeEntryPrice) : 999999;
        contracts = Math.min(riskSize, cashSize, capSize);
    }
    contracts = Math.floor(contracts * 10000) / 10000;
    contracts = Math.min(maxCtr, contracts);
    
    let usedSize = Math.max(minCtr, contracts);
    
    // Stop if you literally can't afford the minimum size anymore
    if ((usedSize * safeEntryPrice) > currentBalance) break;

    let lossPerTrade = usedSize * perShareRisk;
    lossPerTrade = Math.round(lossPerTrade * 100) / 100;

    if (lossPerTrade <= 0) break;

    const nextBalance = Math.round((currentBalance - lossPerTrade) * 100) / 100;
    if (nextBalance <= limit) {
      trades.push({
        trade: tradeCount + 1,
        balanceBefore: currentBalance,
        contracts: usedSize,
        loss: Math.round((currentBalance - limit) * 100) / 100, 
        balanceAfter: limit
      });
      break; 
    }

    trades.push({ trade: tradeCount + 1, balanceBefore: currentBalance, contracts: usedSize, loss: lossPerTrade, balanceAfter: nextBalance });
    currentBalance = nextBalance;
    tradeCount++;
   }
   return { trades, tradeCount: trades.length, suggestedContracts: 0 };
}



// Helpers for popup text
const TWO_LOSS_WARNING_TEXT = `You've had 2 losses in a row.

Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?

Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.

Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.

You've got this. Apply what you learned in the next one!`;

function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">
                On Tilt
              </div>
              <p className="mt-5  text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5  text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>
              <p className="mt-6 text-[14px] font-bold leading-relaxed">
                What to do:  
              </p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>
            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function highlightHundredPercent(line) {
  if (!line) return null;
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">
        100%
      </span>
    ) : (
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

function formatThreeLossMessage(line) {
  if (!line) return null;
  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
    "Continue being Awesome!": "font-bold text-green-600 dark:text-green-400",
    "BIG": "font-bold text-green-600 dark:text-green-400"
  };
  const escaped = Object.keys(highlights).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`(${escaped})`, "g");
  return line.split(regex).map((part, i) =>
    highlights[part]
      ? <span key={i} className={highlights[part]}>{part}</span>
      : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
  );
}

function getDailyGoalMessage() {
  return "Remember, the market will always be there tomorrow.";
}

// --- Helper Functions to generate Popup Content ---
function getWin10NoRulesContent(currentPopup) {
  const message = currentPopup?.data?.message || "";
  const lines = message.split('\n');
  return lines.map((line, i) => {
    if (line.trim() === "Discipline > excitement!") {
      return (
        <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
          Discipline &gt; excitement!<br />
        </span>
      );
    }
    return (
      <React.Fragment key={i}>
        {renderWithTiltTooltip(line)}<br />
      </React.Fragment>
    );
  });
}

function getWin10WithRulesContent(currentPopup) {
  const message = currentPopup?.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.";
  const lines = message.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {formatThreeLossMessage(line)}<br />
    </React.Fragment>
  ));
}

function getTwoLossAfterGoalContent(currentPopup) {
  const message = currentPopup?.data?.message || "You had 2 losses after reaching your daily goal.";
  const lines = message.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {renderWithTiltTooltip(line)}<br />
    </React.Fragment>
  ));
}

function getTwoLossWarningContent() {
  const lines = TWO_LOSS_WARNING_TEXT.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {highlightHundredPercent(line)}<br />
    </React.Fragment>
  ));
}

function getThreeLossWarningContent(currentPopup) {
  const message = currentPopup?.data?.message || "";
  const lines = message.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {formatThreeLossMessage(line)}<br />
    </React.Fragment>
  ));
}


// --- Main Component ---
export default function CalculateFormStocks({ userId, accountId }) {
  const [settings, setSettings] = useState({ ...defaultSettings });
  const [journal, setJournal] = useState(() => ({ ...defaultJournal, Date: getTodayLocalISO() }));
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [calculatedSummary, setCalculatedSummary] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const [showZeroContractsPopup, setShowZeroContractsPopup] = useState(false);
  const [projectionOpen, setProjectionOpen] = useState(false);
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastRiskPercent, setLastRiskPercent] = useState(0);
  const [lastPercentOfBalance, setLastPercentOfBalance] = useState(0);
  const [lastTotalRisk, setLastTotalRisk] = useState(0);
  const [lastActualRiskPercent, setLastActualRiskPercent] = useState(0);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const [userEditingSize, setUserEditingSize] = useState(false);
  const [manualCalculate, setManualCalculate] = useState(false);
  const [autoCalculatedOnce, setAutoCalculatedOnce] = useState(false);
  const [dailyGoalProg, setDailyGoalProg] = useState({ done: 0, target: 0, pct: 0 });
  const [dailyLossProg, setDailyLossProg] = useState({ lost: 0, limit: 0, pct: 0 });
  const [entryStrategies, setEntryStrategies] = useState([]);
  const [exitStrategies, setExitStrategies] = useState([]);
  const [showPTC, setShowPTC] = useState(false);
  const actualBalRef = useRef(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [winProjection, setWinProjection] = useState({ trades: [], tradeCount: 0 });
  const [lossProjection, setLossProjection] = useState({ trades: [], tradeCount: 0, suggestedContracts: 0 });
  const { width, height } = useWindowSize();
  const [showChecklist, setShowChecklist] = useState(false);
  const [userPrefs, setUserPrefs] = useState({});
  const [initialSettings, setInitialSettings] = useState(null);
  const popupsAllowedRef = useRef(false);
  const openedForTodayRef = useRef(false);
  const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";

  // --- Handlers ---
const refreshDailyBars = async (s = settings) => {
    try {
      // 1. Fetch all rows for this account
      const allRows = await fetchJournalRows(userId, accountId);
      const todayISO = getTodayLocalISO();
  
      // 2. Helper to determine if a trade belongs to "Today"
      const isTodayRow = (d) => {
        if (!d) return false;
        if (typeof d === "string") {
          if (d.includes("-")) return d.startsWith(todayISO); // "YYYY-MM-DD"
          if (d.includes("/")) {                               // "MM/DD/YYYY"
            const [mm, dd, yyyy] = d.split("/");
            const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
            return iso === todayISO;
          }
          const dt = new Date(d);
          return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
        }
        if (d?.seconds) {
          return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
        }
        try {
          return new Date(d).toISOString().slice(0,10) === todayISO;
        } catch {
          return false;
        }
      };
  
      // 3. Filter for today's trades and sum the P&L
      const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
      const todayPnL  = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);
  
      const dailyGoal      = Number(s["Daily Goal"] || 0);
      const dailyLossLimit = Number(s["Daily Loss Limit"] || 0);
  
      // 4. Calculate progress amounts
      // If PnL is positive, it counts towards Goal. If negative, it counts towards Loss Limit.
      const positivePnL = Math.max(0, todayPnL); 
      const lossAbs     = Math.max(0, -todayPnL);
  
      // 5. Calculate percentages
      const goalPct = dailyGoal > 0 ? clampPct((positivePnL / dailyGoal) * 100) : 0;
      const lossPct = dailyLossLimit > 0 ? clampPct((lossAbs / dailyLossLimit) * 100) : 0;
  
      // 6. Update State
      setDailyGoalProg({ done: positivePnL, target: dailyGoal,      pct: goalPct });
      setDailyLossProg({ lost: lossAbs,     limit: dailyLossLimit,  pct: lossPct });
    } catch (e) {
      console.error("Error refreshing daily bars:", e);
      setDailyGoalProg({ done: 0, target: Number(settings["Daily Goal"] || 0), pct: 0 });
      setDailyLossProg({ lost: 0, limit: Number(settings["Daily Loss Limit"] || 0), pct: 0 });
    }
  };

  const closeCurrentPopup = () => {
    setCurrentPopup(null);
  };


  const handleChecklistConfirm = () => {
    const today = getTodayLocalISO();
    const newPrefs = { ...userPrefs, "PretradeChecklistShownDate": today, "pretradeChecklistLastShown": today };
    setUserPrefs(newPrefs);
    if (userId) updateUserSettings(userId, newPrefs);
    setShowChecklist(false);
  };

const handleSettingChange = (key, value) => {
    setSettings(prev => {
      const next = { ...prev };
      const riskDollarAliases = new Set(["Risk Dollar Amount", "Risk Amount", "Risk Dollar", "Buying Power"]);
      
      if (riskDollarAliases.has(key)) { 
          next["Risk Dollar Amount"] = value; next["Risk Amount"] = value; next["Risk Dollar"] = value; 
          return next; 
      }

      // --- CLEAR EXITS ON MODE SWITCH ---
      if (key === "Stop Loss Mode") next["Stop Loss"] = "";
      if (key === "Take Profit Mode") next["Take Profit"] = "";

      if (RISK_KEYS.has(key)) { next[key] = value; }
      else if (numericFields.includes(key)) { next[key] = value === "" ? "" : Number(value); }
      else { next[key] = value; }
      
      // --- SYNC BOTH BALANCES INSTANTLY ---
      if (key === "Initial Balance" || key === "Running Balance" || key === "Beginning Balance") { 
          const n = Number(value); 
          const valid = Number.isFinite(n) ? n : 0;
          next["Running Balance"] = valid;
          next["Beginning Balance"] = valid;
      }
      
      return next;
    });
    setCalculatedSummary?.(null);
  };

  const handleJournalChange = (key, value) => setJournal(prev => ({ ...prev, [key]: value }));

  const handleCalculate = async (manual = false, settingsOverride = null) => {
    if (manual) setManualCalculate(true);
    setSavingSettings(true);
    
    // Use override if provided (e.g., from handleSaveTrade), otherwise use current state
    let freshSettings = settingsOverride ? { ...settings, ...settingsOverride } : { ...settings };
    
    try {
      await updateSettings(userId, accountId, freshSettings);

      const runningBalance = Number(freshSettings["Running Balance"] || 0);
      const riskPercent = Number(freshSettings["Risk Percentage"] || 0);
      const riskMode = String(freshSettings["Risk Mode"] || "%");
      const riskDollarAmt = Number(freshSettings["Risk Dollar Amount"] || 0);
      
      const entryPrice = Number(freshSettings["Entry Price"] || 0);
      const stopLossInput = Number(freshSettings["Stop Loss"] || 0);
      const takeProfitInput = Number(freshSettings["Take Profit"] || 0);
      const slMode = String(freshSettings["Stop Loss Mode"] || "Points");
      const tpMode = String(freshSettings["Take Profit Mode"] || "Points");

      let stopLossPerShare = stopLossInput;
      if (slMode === "Price" && entryPrice > 0) {
          stopLossPerShare = Math.abs(entryPrice - stopLossInput);
      } else if (slMode === "Ticks") {
          stopLossPerShare = stopLossInput * 0.01;
      }

      let takeProfitPerShare = takeProfitInput;
      if (tpMode === "Price" && entryPrice > 0) {
          takeProfitPerShare = Math.abs(takeProfitInput - entryPrice);
      } else if (tpMode === "Ticks") {
          takeProfitPerShare = takeProfitInput * 0.01;
      }

      const estimatedStopPrice = entryPrice > 0 ? (entryPrice - stopLossPerShare) : 0;
      const estimatedTargetPrice = entryPrice > 0 ? (entryPrice + takeProfitPerShare) : 0;

 
      const goal = Number(freshSettings["Account Goal"] || 0);

      const allocatedCapital = riskMode === "$" ? riskDollarAmt : runningBalance * (riskPercent / 100);
      const riskPctEquivalent = runningBalance > 0 ? (allocatedCapital / runningBalance) * 100 : 0;
      
      const investLimitValue = Number(freshSettings["Investment Amount"] || 0);
      const investLimitMode = String(freshSettings["Investment Cap Mode"] || "$");
      let investLimitDollar = investLimitValue;
      if (investLimitMode === "%" && runningBalance > 0) {
          investLimitDollar = runningBalance * (investLimitValue / 100);
      }

      let riskRewardRatio = "";
      if (takeProfitPerShare && stopLossPerShare) {
        const rr = takeProfitPerShare / stopLossPerShare;
        riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
      }

      const maxShares = Number(freshSettings["Maximum Shares"] ?? freshSettings["Maximum Contracts"] ?? 100000);
      const minShares = Number(freshSettings["Minimum Shares"] ?? 0.0001);
      

      let shares = 0;
      if (entryPrice > 0 && stopLossPerShare > 0) {
        const riskBasedSize = allocatedCapital / stopLossPerShare;
        const cashBasedSize = runningBalance / entryPrice;
        const investCapSize = investLimitDollar > 0 ? (investLimitDollar / entryPrice) : 999999;
        shares = Math.min(riskBasedSize, cashBasedSize, investCapSize);
      } else if (entryPrice > 0) {
        const cashBasedSize = runningBalance / entryPrice;
        const investCapSize = investLimitDollar > 0 ? (investLimitDollar / entryPrice) : 999999;
        shares = Math.min(cashBasedSize, investCapSize);
      }
      
      shares = Math.floor(shares * 10000) / 10000;
      shares = Math.min(shares, maxShares);
      shares = Math.max(shares, minShares);
      
      let isAffordable = true;
      if ((shares * stopLossPerShare) > allocatedCapital + 0.01 || (shares * entryPrice) > runningBalance + 0.01) {
         isAffordable = false;
      }

      const displayShares = isAffordable ? shares : 0;
      const validShares = isAffordable ? shares : minShares; // Track for zero-popup preview
      
      let totalRisk = validShares * stopLossPerShare;
      let targetReward = validShares * takeProfitPerShare;

      let maxStopLossSize = Math.floor(allocatedCapital * 100) / 100;
      let maxStopLossSizeForMin = minShares > 0 ? Math.floor((allocatedCapital / minShares) * 100) / 100 : 0;
      const actualRiskPercent = runningBalance > 0 ? ((totalRisk / runningBalance) * 100).toFixed(2) : 0;

      setLastRiskPercent(riskPercent);
      setLastPercentOfBalance(allocatedCapital);
      setLastTotalRisk(totalRisk);
      setLastActualRiskPercent(actualRiskPercent);

      setCalculatedSummary({
           pricePerTick: 1,
           riskRewardRatio,
           percentOfBalance: allocatedCapital,
           riskMode,
           riskPctEquivalent,
           riskPercent,
           stopLoss: stopLossPerShare,
           takeProfit: takeProfitPerShare,
           stopLossInput, 
           takeProfitInput,
           slMode,
           tpMode,
           entryPrice,
           estimatedStopPrice,
           estimatedTargetPrice,
           contracts: displayShares, // Using displayShares sets it to 0 seamlessly if unaffordable
           minContracts: minShares,
           totalRisk,
           targetReward,
           maxStopLossSize,
           maxStopLossSizeForMin,
           isAffordable,
           buyingPower: runningBalance, 
           requiredCapital: validShares * entryPrice,
           investmentMode: true
      });

      if (displayShares === 0 && manual) setShowZeroContractsPopup(true); 
      else setShowZeroContractsPopup(false);

      setWinProjection(getCompoundingProjection({
        balance: runningBalance, goal, riskPercent, riskMode, riskDollar: riskDollarAmt,
        stopLoss: stopLossPerShare, takeProfit: takeProfitPerShare, maxContracts: maxShares, minContracts: minShares, pricePerTick: 1, entryPrice, investmentAmount: investLimitValue, investmentCapMode: investLimitMode
      }));
       setLossProjection(getLossProjection({
        balance: runningBalance, riskPercent, riskMode, riskDollar: riskDollarAmt,
        stopLoss: stopLossPerShare, maxContracts: maxShares, minContracts: minShares, pricePerTick: 1, entryPrice, investmentAmount: investLimitValue, investmentCapMode: investLimitMode
       }));      

      if (shares === 0 && manual) setShowZeroContractsPopup(true); 
      else setShowZeroContractsPopup(false);
    } catch (err) { console.error("Settings save failed:", err); }
    if (manualCalculate) setManualCalculate(false);
    setSavingSettings(false);
  };

useEffect(() => {
    const load = async () => {
      try {
        const settingsObj = await getSettings(userId, accountId);
        if (settingsObj) {
            // If Stop Loss is the old default (0.5), update to new (20)
            if (Number(settingsObj["Stop Loss"]) === 0.5) {
                settingsObj["Stop Loss"] = 20;
                settingsObj["Stop Loss Mode"] = "%";
            }
            // If Take Profit is the old default (1), update to new (100)
            if (Number(settingsObj["Take Profit"]) === 1) {
                settingsObj["Take Profit"] = 100;
                settingsObj["Take Profit Mode"] = "%";
            }
            
            // --- FIX: Fill blank Total Equity with Buying Power ---
            if (settingsObj["Beginning Balance"] === undefined && settingsObj["Running Balance"] !== undefined) {
                settingsObj["Beginning Balance"] = settingsObj["Running Balance"];
            }
        }
        setSettings(prev => ({ ...prev, ...(settingsObj || {}) }));
        setInitialSettings(settingsObj || {});
        if (!initialSettings) setInitialSettings(settingsObj);
        setJournal(prev => {
          const cur = String(prev["Timeframe"] || "").trim();
          if (cur) return prev;
          const defTf = String(settingsObj?.["Default Timeframe"] || "").trim();
          return defTf ? { ...prev, "Timeframe": defTf } : prev;
        });
      } catch (err) { console.error("Error loading settings:", err); }
    };
    load();
  }, [userId, accountId]);

  useEffect(() => { if (!userId || !accountId) return; refreshDailyBars(); }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"]]);
  useEffect(() => { if (!userId) return; getUserSettings(userId).then(data => { if (!data || Object.keys(data).length === 0) { const defaultPrefs = { "Pretrade Checklist": "Yes", "PretradeChecklistShownDate": "" }; setUserPrefs(defaultPrefs); updateUserSettings(userId, defaultPrefs); } else { setUserPrefs(data); } }); }, [userId]);
  useEffect(() => { if (currentPopup === null && popupQueue.length > 0) { const nextPopup = popupQueue[0]; setPopupQueue(popupQueue.slice(1)); setCurrentPopup(nextPopup); } }, [popupQueue, currentPopup]);
  useEffect(() => {
    if (!initialSettings || savingSettings || autoCalculatedOnce) return;
    if (!initialSettings["Product"]) return;
    const baseOk = AUTO_CALC_FIELDS.filter(key => key !== "Product" && key !== "Risk Percentage").every(key => typeof initialSettings[key] !== "undefined" && initialSettings[key] !== "" && Number(initialSettings[key]) > 0);
    const riskOk = String(initialSettings["Risk Mode"] || "%") === "$" ? Number(initialSettings["Risk Dollar Amount"] || 0) > 0 : Number(initialSettings["Risk Percentage"] || 0) > 0;
    if (baseOk && riskOk) { handleCalculate(false); setAutoCalculatedOnce(true); }
  }, [initialSettings, savingSettings, autoCalculatedOnce]);
  
  useEffect(() => {
    if (calculatedSummary) {
      setJournal(prev => {
        const next = { ...prev };
        
        // Auto-fill size if user hasn't manually overridden it
        if (calculatedSummary.contracts !== undefined && !userEditingSize) {
          next.Size = calculatedSummary.contracts;
        }
        
        // Auto-fill entry and exit (target) prices
        if (calculatedSummary.entryPrice > 0) {
          next["Entry Price"] = calculatedSummary.entryPrice;
        }
        if (calculatedSummary.estimatedTargetPrice > 0) {
          next["Exit Price"] = calculatedSummary.estimatedTargetPrice;
        }
        
        return next;
      });
    }
  }, [calculatedSummary]);

  useEffect(() => {
    if (!userId) return;
    const today = getTodayLocalISO();
    const enabled = ["yes","true","on","1"].includes(String(userPrefs?.["Pretrade Checklist"] ?? "").toLowerCase());
    const shown = normalizeDateKey(userPrefs?.["PretradeChecklistShownDate"] ?? userPrefs?.["pretradeChecklistLastShown"] ?? "");
    if (shown !== today) { openedForTodayRef.current = false; } else { setShowChecklist(false); openedForTodayRef.current = true; return; }
    if (enabled && !openedForTodayRef.current) { setShowChecklist(true); openedForTodayRef.current = true; }
  }, [userId, userPrefs]);

  useEffect(() => {
    const initial = Number(settings["Initial Balance"] || 0);
    const goal = Number(settings["Account Goal"] || 0);
    const running = Number(settings["Running Balance"] || 0);
    let pct = 0;
    if (goal > initial) { pct = ((running - initial) / (goal - initial)) * 100; }
    pct = Math.max(0, Math.min(100, pct));
    setProgress(pct);
  }, [settings["Initial Balance"], settings["Account Goal"], settings["Running Balance"]]);

const handleSaveTrade = async () => {
    const errors = [];
    
    // [!code highlight] START: Updated Validation & Calc Logic
    // 1. Check which fields are provided
    const abRaw = journal["Actual Balance"];
    const abProvided = abRaw !== "" && abRaw != null && Number.isFinite(Number(abRaw));

    const ticksRaw = journal["Ticks"];
    const ticksProvided = ticksRaw !== "" && ticksRaw != null && Number.isFinite(Number(ticksRaw));

    const isDidNotTake = String(journal["Position"] || "").trim().toLowerCase() === "did not take";

    const jEntry = Number(journal["Entry Price"] || 0);
    const jExit = Number(journal["Exit Price"] || 0);
    const canAutoCalc = jEntry > 0 && jExit > 0;

    // 2. Validate: Must have Balance, Points, OR Prices (unless "Did Not Take")
    if (!isDidNotTake && !abProvided && !ticksProvided && !canAutoCalc) {
        errors.push("- Actual Balance, Points, OR Entry/Exit Prices");
    }

    if (!journal["Date"]) errors.push("- Date");
    if (!settings["Product"]) errors.push("- Product");
    
    // Don't require Win/Loss if we can auto-calculate it from prices
    if (!isDidNotTake && !journal["Win"] && !journal["Loss"] && !canAutoCalc) {
        errors.push("- Win/Loss");
    }
    
    const effectiveContracts = (journal["Size"] !== undefined && journal["Size"] !== "") ? Number(journal["Size"]) : Number(calculatedSummary?.contracts ?? 0);
    if (!Number.isFinite(effectiveContracts) || effectiveContracts <= 0) errors.push("- # of Shares");
    
    if (!calculatedSummary) errors.push("- Click the Calculate button before saving");
    
    if (errors.length > 0) { setMissingFields(errors); return; }
    
    setSavingTrade(true);
    setCurrentPopup({ id: 'tradeSaved' });
    try {
      const tradeNum = await getNextTradeNumber();
      
// [!code highlight] START: Balance Calculation Logic
      const previousRB = Number(settings["Running Balance"] || 0);
      let actualBalanceVal = 0;
      let signedPoints = 0;
      let autoCalcPnl = null;

      if (abProvided) {
          // Case A: User entered Balance directly
          actualBalanceVal = parseCurrency(abRaw);
          const pnl = actualBalanceVal - previousRB;
          if (effectiveContracts > 0) signedPoints = pnl / effectiveContracts;
      } else if (ticksProvided) {
          // Case B: User entered Points -> Calculate Balance
          let points = Number(ticksRaw);
          const isLoss = !!journal["Loss"];
          signedPoints = isLoss ? -Math.abs(points) : Math.abs(points);
          const pnl = effectiveContracts * signedPoints;
          actualBalanceVal = previousRB + pnl;
      } else if (canAutoCalc && !isDidNotTake) {
          // Case C: Auto-calculate from Prices
          const isShort = String(journal["Position"]).trim().toLowerCase() === "sell";
          signedPoints = isShort ? (jEntry - jExit) : (jExit - jEntry);
          autoCalcPnl = signedPoints * effectiveContracts;
          actualBalanceVal = previousRB + autoCalcPnl;
      } else {
          // Fallback (Did Not Take)
          actualBalanceVal = previousRB;
      }
      
      // Determine final Win/Loss for the database
      const finalWin = autoCalcPnl !== null ? (autoCalcPnl > 0) : !!journal["Win"];
      const finalLoss = autoCalcPnl !== null ? (autoCalcPnl < 0) : !!journal["Loss"];
      // [!code highlight] END: Balance Calculation Logic

      const product = settings["Product"] || "";
      
      let tradeObj = {};
            SHEET_HEADERS.forEach((header) => {
              if (header === "Trade #") tradeObj[header] = tradeNum;
              else if (header === "tradeNum") tradeObj["tradeNum"] = tradeNum;
              else if (header === "Product") tradeObj[header] = settings["Product"];
              // [!code highlight] FIXED: Map actualBalanceVal to both Running Balance AND Actual Balance
              else if (header === "Running Balance" || header === "Actual Balance") tradeObj[header] = actualBalanceVal;
              // [!code highlight] ADDED: Explicitly save Balance Before Trade
              else if (header === "Balance Before Trade") tradeObj[header] = previousRB;
              
              else if (header === "Price Per Tick") tradeObj[header] = 1; // Stocks
              else if (header === "Size") tradeObj[header] = journal["Size"];
              else if (header === "Total Risk") {
           const sl = calculatedSummary?.stopLoss || 0; 
           const shares = Number(journal["Size"] || 0);
           tradeObj[header] = shares * sl;
        }
        else if (header === "Percent of Balance") {
           tradeObj[header] = calculatedSummary?.percentOfBalance; 
        }
        else if (header === "Risk To Reward Ratio") {
          tradeObj[header] = calculatedSummary?.riskRewardRatio || "";
        }
        else if (header === "Actual Reward") {
           const prevBal = Number(settings["Running Balance"]);
           tradeObj[header] = Number.isFinite(actualBalanceVal) && Number.isFinite(prevBal) ? (actualBalanceVal - prevBal) : 0;
        }
        else if (header === "Duration (h:mm)") {
          const et = journal["Entry Time"] || "";
          const xt = journal["Exit Time"] || "";
          if (et && xt) {
            let start = timeStringToSeconds(et);
            let end = timeStringToSeconds(xt);
            let diff = end - start;
            if (diff < 0) diff += 24 * 3600;
            tradeObj[header] = secondsToHHMMSS(diff);
          } else {
            tradeObj[header] = "";
          }
        }
        else if (header === "Entry Time") tradeObj[header] = journal["Entry Time"] ? to24HourTime(journal["Entry Time"]) : "";
        else if (header === "Exit Time") tradeObj[header] = journal["Exit Time"] ? to24HourTime(journal["Exit Time"]) : "";
        else if (header === "Actual Percent Growth") {
           const prevBal = Number(settings["Running Balance"] || 0);
           const actualReward = (actualBalanceVal - prevBal);
           tradeObj[header] = prevBal ? ((actualReward / prevBal) * 100).toFixed(2) + "%" : "0.00%";
        }
        else if (header === "Actual Risk to Reward") {
           const shares = Number(journal["Size"] || 0);
           const sl = calculatedSummary?.stopLoss || 0;
           const riskDollar = shares * sl;
           const actualReward = (actualBalanceVal - Number(settings["Running Balance"] || 0));
           let rtr = "";
           if (riskDollar > 0 && actualReward !== 0) {
             const ratio = Math.abs(actualReward) / riskDollar;
             rtr = `${actualReward < 0 ? "-" : ""}${ratio.toFixed(2)}:1`;
           }
           tradeObj[header] = rtr;
        }
        else if (header === "Stop Loss") tradeObj[header] = settings["Stop Loss"];
        else if (header === "Take Profit") tradeObj[header] = settings["Take Profit"];
        else if (header === "Stop Loss_Take Profit") {
           tradeObj[header] = `${settings["Stop Loss"]}/${settings["Take Profit"]}`;
        }
        else if (header === "Suggested Max # of Contracts") {
           tradeObj[header] = calculatedSummary?.contracts ?? "";
        }
        else if (header === "Total Target Reward") {
           tradeObj[header] = calculatedSummary?.targetReward ?? "";
        }
        else if (header === "Win") tradeObj[header] = finalWin;
        else if (header === "Loss") tradeObj[header] = finalLoss;
        else if (header === "Tcks" || header === "Ticks" || header === "Points" || header === "Points / Cents Captured") {
           // Save the signed points used for calc
           tradeObj[header] = signedPoints;
        }
        else if (header === "Lesson") tradeObj[header] = journal["Lesson"] || "";
        else { tradeObj[header] = journal[header] ?? ""; }
      });
      tradeObj["tradeNum"] = tradeNum;
      tradeObj["Timeframe"] = journal["Timeframe"] || "";
      tradeObj["tickSize"] = 0.01; // Universal tick size for all stocks
      tradeObj["assetClass"] = "STOCKS";
      
      await appendTrade(userId, accountId, tradeObj);
      refreshDailyBars();
      if (journal.LogLesson && (journal.Lesson || "").trim()) { try { await appendLesson((journal.Lesson || "").trim()); } catch (err) {} }
      
      // Update BOTH Buying Power and Total Equity immediately
      let updatedSettings = { 
          ...settings, 
          "Running Balance": Number(actualBalanceVal).toFixed(2),
          "Beginning Balance": Number(actualBalanceVal).toFixed(2)
      };
      setSettings(updatedSettings);
      
      // Pass updated settings directly to calculate so it doesn't use stale state
      handleCalculate(false, updatedSettings);
      
      popupsAllowedRef.current = true;
      setJournal(j => ({ ...defaultJournal, Date: getTodayLocalISO(), Size: calculatedSummary?.contracts ?? "", Timeframe: String(settings?.["Default Timeframe"] || "").trim() }));
    } catch (err) { console.error("Trade save failed:", err); } finally { popupsAllowedRef.current = false; setSavingTrade(false); }
  };

  const isDidNotTake =
  (journal["Position"] || "").trim().toLowerCase() === "did not take";

  useBlockWheelOnNumber(actualBalRef);

  // --- POPUP RENDER LOGIC ---
  // This helper function handles all popup rendering logic to keep the return statement clean and error-free.
  const renderPopup = () => {
    if (!currentPopup) return null;

    if (currentPopup.id === 'dailyGoalPopup') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
              <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Congratulations!
            </h2>
            <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
              You reached your <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!<br />
              {currentPopup.data?.dailyGoal !== undefined && (
                <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
                  Goal: <span className="text-green-700 dark:text-green-500">{Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {style: "currency", currency: "USD", minimumFractionDigits: 2})}</span>
                </span>
              )}
              <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
                Take a moment to celebrate your discipline and progress.<br /><br />
                Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
                {getDailyGoalMessage()}
              </span>
            </div>
            <button onClick={closeCurrentPopup} className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'overtradingPopup') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Warning: Overtrading!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span></p>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold">Your maximum trades per day is: </span><span className="font-bold text-yellow-700 dark:text-yellow-300">{currentPopup.data.maxTradesPerDay}</span></p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.</p>
            <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">Review your trades, lock your account, and come back tomorrow recharged for a strong start!</p>
            <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">Below is a really good video from Psychology of Profits regarding protecting your mental capital.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              <a href="https://www.youtube.com/watch?v=6vZG4epFEOM" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition">
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M6 4l10 6-10 6V4z" /></svg>
                Watch: Mental Capital
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
            </div>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'maxLossLimitBlown') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-600 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <div className="flex items-center mb-4">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              <h2 className="text-xl font-bold text-red-600 dark:text-red-400">Account Blown</h2>
            </div>
            <p className="mb-4 font-semibold text-gray-800 dark:text-gray-100">Your balance has reached <span className="font-bold text-red-600">$0</span>. <br/><br/>This is a good time to switch to your demo account to review and refine your plan before depositing more funds.</p>
            <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">The key is to preserve your capital.</p>
            <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">Spend time backtesting until you've mastered your strategy and are consistently profitable. This is the best way to prepare before investing more of your hard-earned money.</p>
            <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">Trading is 80% Psychology. Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              <a href="https://www.youtube.com/@Psychology-Of-Profit" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition">
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M6 4l10 6-10 6V4z" /></svg>
                Visit the Psychology of Profit Youtube Channel
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
            <p className="font-bold mt-4 mb-4 text-red-800 dark:text-red-400">Don't give up. You can do this!</p>
            <button onClick={closeCurrentPopup} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    // Add other popup conditions here following the same pattern...
    if (currentPopup.id === 'tooManyLossesPopup') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Daily Lost Trades Limit Reached!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-red-700 dark:text-red-300">You've hit your max daily loss count.</span></p>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold">Your maximum losses per day is: </span><span className="font-bold text-red-700 dark:text-red-300">{currentPopup.data.maxLossesPerDay}</span></p>
            <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.</p>
            <button onClick={closeCurrentPopup} className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'lossLimitWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" /></svg>
              Daily Loss Limit Exceeded!
            </h2>
            <p className="mb-3 font-semibold text-lg text-gray-800 dark:text-gray-100">Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>, which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.</p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-400">Please consider stopping trading for the day to protect your account.</p>
            <p className="mb-6 font-semibold text-red-700 dark:text-red-400">You are worthy of success! Don't give up!</p>
            <button onClick={closeCurrentPopup} className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === "win10NoRulesPopup") {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Warning - Big Wins Without Following Rules
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
              {(currentPopup.data?.message?.split('\n') || []).map((line, i) => 
                line.trim() === "Discipline > excitement!" 
                  ? (<span key={i} className="font-bold text-blue-700 dark:text-blue-500">Discipline &gt; excitement!<br /></span>) 
                  : (<React.Fragment key={i}>{renderWithTiltTooltip(line)}<br /></React.Fragment>)
              )}
            </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === "win10WithRulesPopup") {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Congratulations – Following Your Rules!
            </h2>
            <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 whitespace-pre-line">
              {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.").split('\n').map((line, i) => (
                <React.Fragment key={i}>{formatThreeLossMessage(line)}<br /></React.Fragment>
              ))}
            </div>
            <button onClick={closeCurrentPopup} className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'moodVolatilityPopup') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/></svg>
              Mood Volatility Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-blue-700 dark:text-blue-300">Your emotional state appears to be heightened.</span></p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Trading with intense emotions—whether it's excitement or stress—can lead to decisions based on feeling, not strategy. Your greatest asset is a calm and objective mind.</p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Before your next trade, journal what you're feeling and why. We recommend you pause until your focus is back on your process.</p>
            <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">If you consistently trade in a heightened emotional state, you may be using a position size, symbol, or timeframe that doesn't fit your personality.</p>
            <button onClick={closeCurrentPopup} className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'twoLossAfterGoalWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Warning – Losses After Goal
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
              {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.").split('\n').map((line, i) => (
                <React.Fragment key={i}>{renderWithTiltTooltip(line)}<br /></React.Fragment>
              ))}
            </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">OK</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'overRiskWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Warning!
            </h2>
            <p className="mb-3 text-gray-800 dark:text-gray-100">You lost more than your planned risk amount.</p>
            <p className="mb-1"><span className="font-extrabold text-yellow-600 dark:text-yellow-400">Risk amount: {formatCurrency(currentPopup.data.riskAmount)}</span></p>
            <p className="mb-4"><span className="font-extrabold text-yellow-600 dark:text-yellow-400">Amount lost: {formatCurrency(currentPopup.data.amountLost)}</span></p>
            <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">Make sure that you are always using the proper stop loss and not moving it. If the trade is going too far against your analysis, you can always get out and get back in at the right time.</p>
            <p className="mb-2 font-extrabold text-yellow-600 dark:text-yellow-400">Keep your losers small and your winners big and never marry a trade idea!</p>
            <button onClick={closeCurrentPopup} className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'drawdownWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/></svg>
              Drawdown Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-purple-700 dark:text-purple-300">Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.</span>
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold">Drawdown amount: </span>
              <span className="font-bold text-purple-700 dark:text-purple-300">${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}</span>
              <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">({currentPopup.data.drawdownPct?.toFixed(2)}%)</span>
            </p>
            <p className="mb-6 text-gray-700 dark:text-gray-300">Consider pausing to review your trades and protect your capital before proceeding.</p>
            <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.</p>
            <button onClick={closeCurrentPopup} className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'consecutiveWinsPopup') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" /></svg>
              Hot Streak!
            </h2>
            <p className="mb-3 text-gray-800 dark:text-gray-100"><span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span></p>
            <p className="text-gray-700 font-semibold dark:text-gray-300">Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.</p>
            <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-blue-300">Stick to your process and keep your edge!</p>
            <button onClick={closeCurrentPopup} className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'accountGoalReached') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" strokeWidth="2" /></svg>
              Account Goal Achieved!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-green-700 dark:text-green-300">Congratulations! You've reached your account goal!</span></p>
            <p className="mb-6 text-gray-700 dark:text-gray-300">Your balance is <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span><br />Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span></p>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-green-700 dark:text-green-300">Your hard work has paid off!</span></p>
            <button onClick={closeCurrentPopup} className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'lessonsLearnedStreakWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M12 6v6l4 2" strokeWidth="2" /></svg>
              Lessons Learned Streak!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span></p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
            <button onClick={closeCurrentPopup} className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'riskOfRuinWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="12" r="10" strokeWidth="2" /></svg>
              Risk of Ruin Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100"><span className="font-bold text-red-700 dark:text-red-300">Based on your current risk, your account could be wiped out in just <span className="underline">{lossProjection.tradeCount}</span> consecutive losses.</span></p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consider lowering your risk per trade to protect your remaining capital.</p>
            <p className="mt-6 mb-6 font-semibold text-gray-700 dark:text-gray-300">Switch to your demo account and trade there until you are winning consistently.</p>
            <p className="mt-6 mb-6 font-semibold text-blue-700 dark:text-blue-400">Don't be discouraged, you can do this!</p>
            <button onClick={closeCurrentPopup} className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'tradeSaved') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
            <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
              <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/><path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
            </span>
            <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">Trade Saved!</div>
            <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">Your journal entry has been saved.</div>
            <button className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition" onClick={closeCurrentPopup}>OK</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'twoLossWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              Warning - 2 Consecutive Losses!
            </h2>
            <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left">
              {getTwoLossWarningContent()}
            </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
          </div>
        </div>
      );
    }

    if (currentPopup.id === 'threeLossWarning') {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /><path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" /></svg>
              Danger!
            </h2>
            <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
              {getThreeLossWarningContent(currentPopup)}
              <div className="mt-4 text-blue-400 font-bold">{"You got this!!"}</div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              {currentPopup.data?.videoLink && (
                <a href={currentPopup.data.videoLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition">
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M6 4l10 6-10 6V4z" /></svg>
                  Watch: Dealing with Trading Losses
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}
              <button onClick={closeCurrentPopup} className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition">Close</button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const entryRequired = (settings["Stop Loss Mode"] === "Price" && Number(settings["Stop Loss"]) > 0) || 
                        (settings["Take Profit Mode"] === "Price" && Number(settings["Take Profit"]) > 0);
  
  const showEntryWarning = entryRequired && !settings["Entry Price"];

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-10">
     {/* Account Settings Card */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 mb-4 animate-fadeIn">
        <div className="flex items-center mb-6"><IconGoal /><h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">Trade Setup (Backtest)</h2></div>
        
        {/* --- COLLAPSIBLE BALANCES SECTION --- */}
        <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span>Account Cash Status</span>
              <span className="text-sm font-normal text-gray-500">
                (Buying Power: <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(settings["Running Balance"])}</span>)
              </span>
            </div>
            <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
            <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
          </summary>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
              <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Account Equity</label>
              <input type="number" value={settings["Beginning Balance"] ?? ""} onChange={e => handleSettingChange("Beginning Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buying Power</label>
              <input type="number" value={settings["Running Balance"] ? Math.round(settings["Running Balance"] * 100) / 100 : ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
            </div>
          </div>
        </details>

        {/* --- STEP 1: THE SETUP --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
          <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
            <div className="bg-gray-300 dark:bg-gray-500 text-white rounded-full p-1 shadow-sm">
                <span className="flex items-center justify-center text-blue-500 dark:text-blue-400 cursor-help">
                  <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="16" width="16" xmlns="http://www.w3.org/2000/svg"><path d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248 111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"></path></svg>
                </span>
            </div>
            <p className="text-[13px] font-bold text-blue-700 dark:text-blue-300">
                This calculator is currently configured for <span className="underline">Buying</span> Stocks only. <span className="text-blue-500 dark:text-blue-400 font-normal ml-1">Shorting capabilities are coming soon!</span>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Ticker Symbol<span className="text-red-500">*</span></label>
              <input type="text" placeholder="e.g. AAPL" value={settings["Product"] || ""} onChange={e => handleSettingChange("Product", e.target.value.toUpperCase())} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
                Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
              </label>
              <input 
                type="number" 
                placeholder="e.g. 4500.25" 
                value={settings["Entry Price"] || ""} 
                onChange={e => handleSettingChange("Entry Price", e.target.value)} 
                className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${
                  showEntryWarning 
                    ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                    : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
                }`} 
              />
              {showEntryWarning && (
                <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
                  Entry Price required for Price-based exits
                </p>
              )}
            </div>
          </div>
        </div>

        {/* --- STEP 2: EXITS --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
          <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500">*</span></label>
                  <Tooltip 
                    variant="blue" 
                    backdrop 
                    content={
                      <div className="space-y-3 p-1">
                        <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                          Speed Tip
                        </div>
                        <p className="text-[15px] leading-relaxed text-gray-100">
                          In fast markets, you don't need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">$0.10-0.25$</span> pts on low-priced stocks, <span className="font-bold text-white">$1.00-2.00$</span> pts on high-beta names).
                        </p>
                        <p className="text-[15px] font-bold text-blue-300">
                          Backtesting your strategy is an excellent way to determine that number.
                        </p>
                      </div>
                    }
                  >
                    <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </div>
              <select 
                  value={settings["Stop Loss Mode"] || "Points"} 
                  onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Points">by Points ($)</option>
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input type="number" placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : (settings["Stop Loss Mode"] === "Ticks" ? "Ticks" : "Points")} value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400"/>
              
              {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Stop Loss Mode"] === "Price" 
                    ? `Points: ${Math.abs(settings["Entry Price"] - settings["Stop Loss"]).toFixed(2)}`
                    : settings["Stop Loss Mode"] === "Ticks"
                    ? `Est. Stop Price: ${formatCurrency(Number(settings["Entry Price"]) - (Number(settings["Stop Loss"]) * 0.01))}`
                    : `Est. Stop Price: ${formatCurrency(Number(settings["Entry Price"]) - Number(settings["Stop Loss"]))}`}
                </p>
              )}
            </div>

            <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
              <div className="flex justify-between items-center mb-2">
                <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
                <select 
                  value={settings["Take Profit Mode"] || "Points"} 
                  onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Points">by Points ($)</option>
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input type="number" placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : (settings["Take Profit Mode"] === "Ticks" ? "Ticks" : "Points")} value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
              
              {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Take Profit Mode"] === "Price" 
                    ? `Points: ${Math.abs(settings["Take Profit"] - settings["Entry Price"]).toFixed(2)}`
                    : settings["Take Profit Mode"] === "Ticks"
                    ? `Est. Target Price: ${formatCurrency(Number(settings["Entry Price"]) + (Number(settings["Take Profit"]) * 0.01))}`
                    : `Est. Target Price: ${formatCurrency(Number(settings["Entry Price"]) + Number(settings["Take Profit"]))}`}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* --- STEP 3: CAPITAL LIMITS (Stocks) --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
          <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Capital Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget</label>
                  <Tooltip variant="neutral" backdrop content="The maximum dollar amount or % you are willing to lose if your stop loss is hit.">
                    <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </div>
                <select value={settings["Risk Mode"]||"$"} onChange={e=>handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
                  <option value="$">$ Amount</option>
                  <option value="%">% of Balance</option>
                </select>
              </div>
              <input type="number" placeholder={settings["Risk Mode"] === "%" ? "e.g. 1" : "e.g. 100"} value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Investment Cap</label>
                  <Tooltip variant="neutral" backdrop content="The maximum amount of total cash (buying power) you are willing to tie up in this one position.">
                    <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </div>
                <select value={settings["Investment Cap Mode"]||"$"} onChange={e=>handleSettingChange("Investment Cap Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
                  <option value="$">$ Amount</option>
                  <option value="%">% of Balance</option>
                </select>
              </div>
              <input type="number" placeholder="Optional Cap" value={settings["Investment Amount"] || ""} onChange={e => handleSettingChange("Investment Amount", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
        </div>

        {/* --- PROJECTIONS ROW --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
           <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
              <input 
                type="number" 
                value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} 
                disabled 
                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" 
              />
           </div>
           <button 
             type="button" 
             className={`w-full h-[42px] mt-auto bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} 
             onClick={() => setProjectionOpen(true)} 
             disabled={!calculatedSummary}
           >
             Win Projections
           </button>
           <button 
             type="button" 
             className={`w-full h-[42px] mt-auto bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} 
             onClick={() => setLossProjectionOpen(true)} 
             disabled={!calculatedSummary}
           >
             Loss Projections
           </button>
        </div>

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />
        
        {/* Progress Bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Goal</span>
              <span>{formatCurrency(dailyGoalProg.done)} / {formatCurrency(dailyGoalProg.target)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${dailyGoalProg.pct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Loss Limit</span>
              <span>{formatCurrency(dailyLossProg.lost)} / {formatCurrency(dailyLossProg.limit)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-500" style={{ width: `${dailyLossProg.pct}%` }} />
            </div>
          </div>
        </div>
        
        <div className="mt-8 mb-2">
          <div className="flex items-center gap-4">
            <span className="font-bold text-gray-800 dark:text-gray-100">Goal Progress</span>
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-3 rounded-lg relative overflow-hidden">
              <div className="bg-green-500 h-3 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{Math.round(progress)}%</span>
          </div>
        </div>

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        {/* --- SUMMARY BOX --- */}
        <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4 mt-4">
          <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white gap-2"><IconGoal />Summary</h3>
          {calculatedSummary ? (
            <>
              <SummaryRow label="Reward to Risk Ratio" value={calculatedSummary.riskRewardRatio} />
              
              <SummaryRow 
                label={
                  <span className="flex items-center gap-2">
                    {settings["Risk Mode"] === "%" ? `${settings["Risk Percentage"]}% of Balance` : `Risk Amount ($)`}
                  </span>
                } 
                value={formatCurrency(calculatedSummary.percentOfBalance)} 
              />

              {calculatedSummary.entryPrice > 0 && (
                <SummaryRow 
                  label="Position Value (Cash Required)" 
                  value={formatCurrency(calculatedSummary.contracts * calculatedSummary.entryPrice)} 
                />
              )}

    
                <SummaryRow 
                  label="Stop Loss" 
                  value={(() => {
                    const slVal = calculatedSummary.stopLossInput;
                    if (!slVal || slVal <= 0) return "–";
                    const entry = calculatedSummary.entryPrice;
                    const getDistStr = (d, parens) => {
                        let str = `${Math.round(d * 100) / 100} pts / ${Math.round(d * 100)} ticks`;
                        return parens ? `(${str})` : str;
                    };
                    if (calculatedSummary.slMode === "Price") {
                      const dist = entry > 0 ? Math.abs(entry - slVal) : null;
                      return <span className="flex items-center gap-1">{formatCurrency(slVal)} {dist !== null ? getDistStr(dist, true) : ""}</span>;
                    }
                    let ptsDist = calculatedSummary.slMode === "Ticks" ? slVal * 0.01 : slVal;
                    if (entry > 0) {
                      const price = entry - ptsDist;
                      return <span className="flex items-center gap-1">{formatCurrency(price)} {getDistStr(ptsDist, true)}</span>;
                    }
                    return getDistStr(ptsDist, false);
                  })()}
                />
                <SummaryRow 
                  label="Take Profit" 
                  value={(() => {
                    const tpVal = calculatedSummary.takeProfitInput;
                    if (!tpVal || tpVal <= 0) return "–";
                    const entry = calculatedSummary.entryPrice;
                    const getDistStr = (d, parens) => {
                        let str = `${Math.round(d * 100) / 100} pts / ${Math.round(d * 100)} ticks`;
                        return parens ? `(${str})` : str;
                    };
                    if (calculatedSummary.tpMode === "Price") {
                      const dist = entry > 0 ? Math.abs(tpVal - entry) : null;
                      return <span className="flex items-center gap-1">{formatCurrency(tpVal)} {dist !== null ? getDistStr(dist, true) : ""}</span>;
                    }
                    let ptsDist = calculatedSummary.tpMode === "Ticks" ? tpVal * 0.01 : tpVal;
                    if (entry > 0) {
                      const price = entry + ptsDist;
                      return <span className="flex items-center gap-1">{formatCurrency(price)} {getDistStr(ptsDist, true)}</span>;
                    }
                    return getDistStr(ptsDist, false);
                  })()}
                />
     

              <SummaryRow
                label={
                  <span className="inline-flex items-center gap-2">
                    Projected Loss (at Stop)
                    <Tooltip variant="neutral" backdrop content={
                        <div className="space-y-2">
                          <div className="font-bold text-blue-400">Calculation Details</div>
                          <div>${calculatedSummary.stopLoss.toFixed(2)} risk/share × {calculatedSummary.contracts} shares = <strong>{formatCurrency(calculatedSummary.totalRisk)}</strong></div>
                          <div className="text-[13px] text-gray-400 italic">Note: You pay upfront for the shares, but you only lose the difference between Entry and Stop Loss.</div>
                        </div>
                    }>
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">?</span>
                    </Tooltip>
                  </span>
                }
                value={formatCurrency(calculatedSummary.totalRisk)}
                red={calculatedSummary.contracts === 0}
              />
              
              <SummaryRow label="Target Reward" value={formatCurrency(calculatedSummary.targetReward)} />
              
              <div className={calculatedSummary.contracts === 0 ? "rounded-md px-2 py-1 bg-red-50 dark:bg-red-900/40" : "rounded-md px-2 py-1 bg-blue-300 dark:bg-blue-400/30"}>
                <SummaryRow label={<span className="text-gray-600 dark:text-gray-300">Suggested # of Shares</span>} value={calculatedSummary.contracts === 0 ? <span className="text-red-600 font-bold">0</span> : <span className="text-gray-600 dark:text-gray-300 font-semibold">{calculatedSummary.contracts}</span>} red={calculatedSummary.contracts === 0} />
              </div>
            </>
          ) : (<div className="text-gray-400 dark:text-gray-600 text-center py-6">No calculations yet. Fill out the form above and click Calculate.</div>)}
        </div>

        <div className="flex flex-col items-end gap-2 mt-8">
          {(() => {
            let msg = "";
            if (!settings["Product"]) msg = "Enter a Ticker Symbol";
            else if (!Number(settings["Entry Price"])) msg = "Enter an Entry Price";
            else {
              const mode = String(settings["Risk Mode"] || "%");
              const amt = mode === "$" ? Number(settings["Risk Dollar Amount"]) : Number(settings["Risk Percentage"]);
              if (amt <= 0) msg = "Investment Amount must be > 0";
              else if (Number(settings["Stop Loss"]) <= 0) msg = "Stop Loss must be > 0";
              else if (Number(settings["Take Profit"]) <= 0) msg = "Take Profit must be > 0";
            }

            if (msg) {
              return <span className="text-sm font-semibold text-red-500 animate-pulse">{msg}</span>;
            }
            return null;
          })()}

          <button 
            type="button" 
            onClick={() => handleCalculate(true)} 
            disabled={
              savingSettings || 
              !settings["Product"] || 
              !Number(settings["Entry Price"]) ||
              (String(settings["Risk Mode"] || "%") === "$" 
                ? Number(settings["Risk Dollar Amount"]) <= 0 
                : Number(settings["Risk Percentage"]) <= 0
              ) ||
              Number(settings["Stop Loss"]) <= 0 ||
              Number(settings["Take Profit"]) <= 0
            }
            className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-800 dark:to-blue-500 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>
)} {/* <--- ADD THIS CLOSING BRACE/PARENTHESIS HERE */}

      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4"><IconJournal /><div><h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3><p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Log your trade details below. <span className="text-red-500">* denotes required</span></p></div></div>
        <div className="space-y-8">
          <div><h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">1. Setup & Context</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500">*</span></label><input id="date" type="date" value={journal["Date"] || getTodayLocalISO()} onChange={e => handleJournalChange("Date", e.target.value)} className={`w-full h-[42px] px-4 py-2 rounded-lg border bg-gray-50 dark:bg-gray-700 dark:text-white transition focus:ring-2 focus:ring-blue-400 ${missingFields.includes("Date") ? "border-red-500" : "border-gray-200 dark:border-gray-700"}`} /></div>
              <div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label><select id="session" value={journal["Session"]} onChange={e => handleJournalChange("Session", e.target.value)} className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"><option value="">Select</option>{SESSION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
              <div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label><input id="timefram" type="text" list="timeframe-options" value={journal["Timeframe"]} onChange={e => handleJournalChange("Timeframe", e.target.value)} placeholder="e.g. 5m" className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" /><datalist id="timeframe-options">{TIMEFRAME_OPTIONS.map(tf => <option key={tf} value={tf} />)}</datalist></div>
              <div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label><select id="position" value={journal["Position"]} onChange={e => handleJournalChange("Position", e.target.value)} className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"><option value="">Select</option>{POSITION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            </div>
          </div>
          <div><h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">2. Execution</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700"><label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label><div className="space-y-4"><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label><div className="relative flex items-center"><input id="entryTime" type="time" ref={entryTimeRef} value={to24HourTime(journal["Entry Time"])} onChange={e => setJournal(prev => ({ ...prev, "Entry Time": toAmPm(e.target.value) }))} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /><FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()} /></div></div><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label><input id="entryPrice" type="number" placeholder="e.g. 4523.25" value={journal["Entry Price"] ?? ""} onChange={e => handleJournalChange("Entry Price", e.target.value)} className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /></div><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label><input id="entryStrategy" type="text" list="entry-strategy-options" value={journal["Entry Strategy"]} onChange={e => handleJournalChange("Entry Strategy", e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /><datalist id="entry-strategy-options">{entryStrategies.map((s) => <option key={s} value={s} />)}</datalist></div></div></div>
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700"><label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label><div className="space-y-4"><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label><div className="relative flex items-center"><input id="exitTime" type="time" ref={exitTimeRef} value={to24HourTime(journal["Exit Time"])} onChange={e => setJournal(prev => ({ ...prev, "Exit Time": toAmPm(e.target.value) }))} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /><FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={() => exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()} /></div></div><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label><input id="exitPrice" type="number" placeholder="e.g. 4527.50" value={journal["Exit Price"] ?? ""} onChange={e => handleJournalChange("Exit Price", e.target.value)} className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /></div><div><label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label><input id="exitStrategy" type="text" list="exit-strategy-options" value={journal["Exit Strategy"]} onChange={e => handleJournalChange("Exit Strategy", e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" /><datalist id="exit-strategy-options">{exitStrategies.map((s) => <option key={s} value={s} />)}</datalist></div></div></div>
              <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800"><label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Quantity DETAILS</label><div className="space-y-4"><div><label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200"># of Shares<span className="text-red-500">*</span></label><input id="shares" type="number" step="any" min={0} max={settings["Maximum Contracts"] || 2000} value={journal["Size"] !== undefined && journal["Size"] !== "" ? journal["Size"] : calculatedSummary?.contracts ?? ""} onChange={e => { handleJournalChange("Size", e.target.value); setUserEditingSize(true); }} className={`w-full px-4 py-2 rounded-lg border ${missingFields.includes("- # of Shares") ? "border-red-500" : "border-gray-200 dark:border-gray-600"} focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono`} placeholder="0" /></div>
              <div>
                <label className="block font-bold mb-1 mt-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  Ideal Stop Loss
                  <Tooltip
                    variant="blue"
                    backdrop
                    content={
                      <div className="space-y-2">
                        <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                          Ideal Stop Loss
                        </div>
                        <p className="text-[14px] leading-relaxed">
                          You should always use your strategy for setting the proper stop loss. You may notice that the stop loss is too big or too small.
                        </p>
                        <p className="text-[14px] leading-relaxed">
                          You can use this field to help track a more ideal stop loss for the trade and see the results on the Stats page.
                        </p>
                      </div>
                    }
                  >
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                      ?
                    </span>
                  </Tooltip>
                </label>
                <input
                  id="ideal"
                  type="number"
                  value={journal["IdealSL"] ?? ""}
                  onChange={e => handleJournalChange("IdealSL", e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                  placeholder="Qty"
                />
              </div>
              <div hidden><label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">Points / Cents Captured</label><input id="points" type="number" value={journal["Ticks"] || ""} onChange={e => handleJournalChange("Ticks", e.target.value)} className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono" placeholder="e.g. 1.50" /></div><div hidden><label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2">Potential Points<Tooltip variant="neutral" content={<div className="text-sm">This field captures the potential points that could have been captured with the trade if you stayed in longer.</div>}><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">?</span></Tooltip></label><input id="potentialPoints" type="number" value={journal["Potential"] || ""} onChange={e => handleJournalChange("Potential", e.target.value)} className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono" placeholder="Qty" /></div></div></div>
            </div>
          </div>
           {/* SECTION 3: OUTCOME */}
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">
        3. Outcome
      </h4>

      {/* 3a. Win/Loss Buttons */}
      <div className="mb-6">
        <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
          Result{!isDidNotTake && <span className="text-red-500">*</span>}
        </label>
        <div className="flex gap-4 max-w-md">
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: true, Loss: false }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Win"] 
                ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            WON
          </button>
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: false, Loss: true }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Loss"] 
                ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            LOSS
          </button>
        </div>
      </div>

{/* 3b. Backtesting Instructions */}
      <div className="border border-yellow-300 dark:border-yellow-600 rounded-xl p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/10">
        <div className="text-yellow-800 dark:text-yellow-200 mb-2 text-sm font-semibold flex items-start gap-2">
          <span className="mt-1">💡</span>
          <div>
            Leave blank to <strong>Auto-Calculate!</strong>
            <p className="font-normal mt-1 opacity-90 leading-relaxed">
              If your <strong>Entry Price</strong>, <strong>Exit Price</strong>, and <strong># of Shares</strong> are filled out, you can leave these fields blank. We will automatically calculate your Win/Loss, PnL, and new balance when you save.
              <br/><br/>
              Alternatively, you can override this by manually entering your <strong>New Balance</strong> OR <strong>Points Captured</strong> below.
            </p>
          </div>
        </div>
      </div>

      {/* 3c. Balance / Ticks Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
        
        {/* New Balance */}
        <div>
           <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance</label>
           <div className="relative">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
             <input
                id="actual-balance-input"
                type="text"
                inputMode="decimal"
                value={journal["Actual Balance"] ?? ""}
                // [!code highlight] START: Clear Ticks on Change
                onChange={(e) => {
                  const raw = e.target.value;
                  setJournal((j) => ({
                    ...j,
                    ["Actual Balance"]: raw,
                    ["Ticks"]: raw ? "" : j["Ticks"], // Clear Ticks if Balance entered
                  }));
                }}
                onBlur={(e) => {
                   const raw = String(e.target.value ?? "").trim();
                   setJournal((j) => ({
                     ...j,
                     ["Actual Balance"]: (raw === "" || String(j["Ticks"] ?? "").trim() !== "")
                       ? ""
                       : parseCurrency(raw),
                   }));
                 }}
                // [!code highlight] END
                className={`w-full pl-8 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
                  missingFields.includes("- Actual Balance or Points")
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-500"
                }`}
                placeholder="0.00"
              />
           </div>
        </div>

        {/* Ticks/Points */}
        <div>
          <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
             Points Captured
          </label>
          <input
            id="ticks"
            type="number"
            value={journal["Ticks"] || ""}
            // [!code highlight] START: Clear Balance on Change
            onChange={(e) => {
              const val = e.target.value;
              setJournal((j) => ({
                ...j,
                ["Ticks"]: val,
                ["Actual Balance"]: val ? "" : j["Actual Balance"], // Clear Balance if Points entered
              }));
            }}
            // [!code highlight] END
            className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-yellow-400 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
              missingFields.includes("- Actual Balance or Points")
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-500"
            }`}
            placeholder="Qty"
            inputMode="decimal"
          />
        </div>

      </div>
    </div>
          <div><h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">4. Psychology & Review</h4><div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6"><div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?</label><select id="rules" value={journal["Did I follow my Rules?"]} onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)} className={`w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition ${missingFields.includes("- Did I follow my Rules?") ? "border-red-500" : "border-gray-200 dark:border-gray-700"}`}><option value="">Select</option>{RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label><select id="mood"value={journal["Mood_Mindset"]} onChange={e => handleJournalChange("Mood_Mindset", e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"><option value="">Select (1=Calm, 5=Stress)</option>{MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div><div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label><select id="grade" value={journal["Grade"]} onChange={e => handleJournalChange("Grade", e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"><option value="">Select</option>{GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div></div><div className="space-y-6"><div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label><input id="link" type="text" value={journal["Links_Screenshots"]} onChange={e => handleJournalChange("Links_Screenshots", e.target.value)} placeholder="Paste URL here..." className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" /></div><div><label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label><textarea rows={3} value={journal["Notes"]} onChange={e => handleJournalChange("Notes", e.target.value)} placeholder="Anything to note about this trade? How were you feeling physically?" className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" /></div><div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50"><label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">Lesson (Optional)</label><textarea rows={2} value={journal["Lesson"]} onChange={e => handleJournalChange("Lesson", e.target.value)} placeholder="What did you learn from this trade?" className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y" /><div className="mt-3"><label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none"><input type="checkbox" checked={journal["LogLesson"]} onChange={e => handleJournalChange("LogLesson", e.target.checked)} className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />Save to Lessons Database</label></div></div></div></div>
        <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700"><button className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-700 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleSaveTrade} disabled={savingTrade}>{savingTrade ? "Saving..." : "Save Trade"}</button>
      </div>
      </div>


      {projectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b] max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
            <button className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800" onClick={() => setProjectionOpen(false)} aria-label="Close">×</button>
            <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2"><circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" /><path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" /></svg>
              Win Projection Table
            </h3>

            {/* --- NEW: Explanatory Text --- */}
            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many trades it would take to reach your goal based on your current settings. Any trades with the red background indicate when you are over your risk tolerance. The totals are estimates and don't take into account broker fees and commissions.
            </p>

            {/* --- NEW: Dynamic Time Estimation Block --- */}
            {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const trades = winProjection?.trades || [];
              const tradeCount = winProjection?.tradeCount || 0;

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

              let estimatedDays = 0;
              let currentDayAccumulator = 0;

              if (dailyGoal > 0) {
                for (const t of trades) {
                  const profit = t.tradeProfit || 0;
                  if (profit >= dailyGoal) {
                    estimatedDays++;
                    currentDayAccumulator = 0;
                  } else {
                    currentDayAccumulator += profit;
                    if (currentDayAccumulator >= dailyGoal) {
                      estimatedDays++;
                      currentDayAccumulator = 0;
                    }
                  }
                }
                if (currentDayAccumulator > 0) estimatedDays++;
              } else {
                estimatedDays = tradeCount;
              }

              return (
                <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                    <FaRegClock className="text-blue-500" />
                    Estimated Time to Goal
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                        {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades.
                      </p>
                    </div>

                    <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                      <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>
                          While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.
                        </li>
                        <li>
                          Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.
                        </li>
                        <li>
                          As your balance compounds, your position sizes and profits will <strong>increase</strong>. Make sure to update your <strong>Daily Goal</strong> in the settings to reflect your growing account size!
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-auto max-h-[50vh]">
              <table className="min-w-full border-separate border-spacing-0 rounded-xl shadow bg-white/95 dark:bg-gray-900/90 border-2 border-blue-200 dark:border-blue-800">
                {/* ... existing table code ... */}
                <thead>
                  <tr className="bg-blue-100 dark:bg-blue-900/80">
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Investment</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold"># Shares</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Profit</th>
                    <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let runningBal = Number(settings["Running Balance"] || 0);
                    const rowsToShow = isTrialing 
                ? winProjection.trades.slice(0, 10) 
                : winProjection.trades;

              return rowsToShow.map((row, i) => {
                      const before = runningBal;
                      const after = before + (row.tradeProfit ?? 0);
                      runningBal = after;
                      return (
                        <tr key={i} className={`${row.underfunded ? "bg-red-100 dark:bg-red-900/80" : "hover:bg-blue-50 dark:hover:bg-blue-800/80"} transition`}>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{row.trade}</td>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(before)}</td>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar)}</td>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{row.contracts}</td>
                          <td className={`px-3 py-2 border-b ${row.contracts === 0 ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>{formatCurrency(row.totalRisk)}</td>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit)}</td>
                          <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(after)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
                  {/* --- NEW: Trial Indicator --- */}
                          {isTrialing && winProjection.trades.length > 5 && (
                            <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                              <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                                Full projection table available after Free Trial
                              </p>
                            </div>
                          )}
                        </div>
                </div>
              </div>
            )}

{lossProjectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616] max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
            <button className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800" onClick={() => setLossProjectionOpen(false)} aria-label="Close">×</button>
            <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2"><circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" /><path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" /></svg>
              Loss Projection Table
            </h3>

            {/* --- NEW: Explanatory Text --- */}
            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many consecutive full-loss trades it would take to blow your account based on your current settings. The totals are estimates and do not include fees.
            </p>

            {/* --- NEW: Dynamic Summary Block (Risk Analysis) --- */}
            {(() => {
              const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const tradeCount = lossProjection?.tradeCount || 0;

              if (!(dailyLoss > 0)) return null;

              // Prop (Max Loss) vs Retail ($0 Balance) logic
              let daysToRuin = 0;
              let ruinThreshold = 0;
              let isProp = (init > 0 && maxLoss > 0);

              if (isProp) {
                ruinThreshold = Math.max(0, init - maxLoss);
                const distance = Math.max(0, running - ruinThreshold);
                daysToRuin = Math.ceil(distance / dailyLoss);
              } else {
                // Retail logic: ruin is at $0
                ruinThreshold = 0;
                daysToRuin = Math.ceil(running / dailyLoss);
              }

              return (
                <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                  <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                    </svg>
                    Risk Analysis
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                        {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                      </p>
                    </div>

                    <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                      <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                        {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                        If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-auto max-h-[50vh]">
              <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                {/* ... existing table code ... */}
                <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold"># Shares</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Loss Amount</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {(lossProjection?.trades?.length ?? 0) === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">No trades can be taken with the current settings.</td></tr>
                  ) : (
                    (isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades).map((row, i) => (
                      <tr key={i} className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group">
                        <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{row.trade}</td>
                        <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{formatCurrency(row.balanceBefore)}</td>
                        <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{row.contracts}</td>
                        <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">{formatCurrency(row.loss)}</td>
                        <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{formatCurrency(row.balanceAfter)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
        {/* --- NEW: Trial Indicator --- */}
        {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
              Full projection table available after Free Trial
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
)}


{missingFields.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setMissingFields([])}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
              </svg>
              Required Fields Missing
            </h2>
            <ul className="mb-4 text-gray-800 dark:text-gray-200 list-none space-y-1">
              {missingFields.map((f) => (
                <li key={f} className="pl-2">{f}</li>
              ))}
            </ul>
            <button
              onClick={() => setMissingFields([])}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow w-full sm:w-auto"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showZeroContractsPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => setShowZeroContractsPopup(false)} aria-label="Close">×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/></svg>
              {calculatedSummary?.contracts === 0 ? "Warning: Zero Shares" : "Insufficient Funds"}
            </h2>
            {calculatedSummary?.contracts === 0 ? (
              <>
                <p className="mb-3 text-gray-800 dark:text-gray-100">Based on your account balance, risk tolerance, and stop loss size, the suggested number of shares is <span className="font-bold text-red-500">0</span>.</p>
                <p className="mb-6 text-gray-700 dark:text-gray-300">Consider increasing your risk percentage or finding a trade with a tighter stop loss.</p>
              </>
            ) : (
              <>
                <p className="mb-4 text-gray-800 dark:text-gray-100">Your risk plan allows for <span className="font-bold text-blue-600 dark:text-blue-400">{calculatedSummary?.contracts} shares</span>, but you do not have enough Buying Power to take this trade.</p>
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 mb-4 text-sm">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600 dark:text-gray-400">Required Capital:</span>
                    <span className="font-bold text-red-600 dark:text-red-400">{formatCurrency(calculatedSummary?.requiredCapital)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Available Buying Power:</span>
                    <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(calculatedSummary?.buyingPower)}</span>
                  </div>
                </div>
                <p className="mb-2 font-bold text-gray-700 dark:text-gray-200">What can you do?</p>
                <ul className="list-disc pl-5 mb-6 text-gray-700 dark:text-gray-300 space-y-1">
                  <li>Deposit more funds.</li>
                  <li>Reduce your position size manually.</li>
                  <li>Use a margin account (update Leverage in settings).</li>
                </ul>
              </>
            )}
            <button onClick={() => setShowZeroContractsPopup(false)} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition w-full sm:w-auto">Close</button>
          </div>
        </div>
      )}

      {showChecklist && <PreTradeChecklistModal onClose={handleChecklistConfirm} onConfirm={handleChecklistConfirm} />}
      
      <button onClick={() => setShowCalculator(true)} title="Open Calculator" className="fixed bottom-12 right-6 z-40 bg-gray-800 dark:bg-gray-700 text-white rounded-full p-4 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-transform transform hover:scale-110">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </button>
      
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
      
      {/* Popups handled via helper function */}
      {renderPopup()}
    </div>
  </div>
  );
}